import { BlockError } from '@/blocks.04-project/block-error/block-error';
import merge from 'lodash-es/merge';
import isArray from 'lodash-es/isArray';
import toArray from 'lodash-es/toArray';
import isEmpty from 'lodash-es/isEmpty';
import filter from 'lodash-es/filter';
import forEach from 'lodash-es/forEach';
import pick from 'lodash-es/pick';
import isNull from 'lodash-es/isNull.js';

/** @typedef {import("./animation.types.js").RemoveOptions} */

/***
 * Класс анимаций
 * Методы:
 * show(elements, options) - показать элемент
 * hide(elements, options) - скрыть элемент
 * toggle(elements, options, toggle) - переключение элементов
 *
 * классы:
 * animation-hidden - скрытие элемента после или до анимации
 *
 * @example
 * // !!! Перед показом элемента на элементе должен быть класс animation-hidden !!!
 *
 * // Показ по селектору
 * window.Block.Animation.show('.product-card', {
 *   fade: true
 *   afterAnimation: (elements) => elements[0].remove()
 * })
 *
 * // Скрытие элемента
 * const elem = document.querySelector('#test');
 * window.Block.Animation.hide(elem, {
 *   direction: 'right'
 * })
 *
 * // Переключение элементов
 * const elements = document.querySelectorAll('.product-card');
 * window.Block.Animation.toggle(elements, {
 *   updateSlider: true
 *   afterAnimation: (elements) => elements[0].remove()
 * })
 */
class Animation {
	static _BlockError = new BlockError({ blockName: 'Animation' });

	/**
	 * Переключение видимости элементов
	 * @param elements {HTMLElement|HTMLElement[]|String} - HTML элемент/ы или селектор
	 * @param options {RemoveOptions} - Параметры анимации
	 * @param toggle {boolean|null} - Переключение. Если не передать определяется за счёт наличия animation-hidden
	 */
	static toggle = (elements, options = {}, toggle = null) => {
		try {
      const newElements = this._getElements(elements);
      const newOptions = this._getOptions(options);
			if (!isNull(toggle)) {
				if (toggle)	this._show(newElements, newOptions);
				else this._hide(newElements, newOptions);
				return;
			}
			forEach(newElements, (elem) => {
				if (elem.classList.contains('animation-hidden')) this.show(elem, options);
				else this.hide(elem, options);
			});
		} catch (err) {
			console.error(err);
		}
	};

	/**
	 * Показ элементов на страницы
	 * @param elements {HTMLElement|HTMLElement[]|String} - HTML элемент/ы или селектор
	 * @param options {RemoveOptions} Параметры анимации
	 */
	static show = (elements, options = {}) => {
		try {
			this._show(this._getElements(elements), this._getOptions(options));
		} catch (err) {
			console.error(err);
		}
	};

	/**
	 * Скрытие элементов со страницы
	 * @param elements {HTMLElement|HTMLElement[]|String} - HTML элемент/ы или селектор
	 * @param options {RemoveOptions} Параметры анимации
	 */
	static hide = (elements, options = {}) => {
		try {
			this._hide(this._getElements(elements), this._getOptions(options));
		} catch (err) {
			console.error(err);
		}
	};

	/**
	 * Метод показа элементов
   * @param elements {HTMLElement[]} - HTML элементы
   * @param options {RemoveOptions} Параметры анимации
	 * @private
	 */
	static _show = (elements, options) => {
		forEach(elements, (elem) => {
			if (!elem.classList.contains('animation-hidden')) return;
			this._clearClasses(elem);
			elem.classList.add('animation-show-start');
			const width = elem.offsetWidth;
			const height = elem.offsetHeight;
			const params = {
				...pick(options, ['duration', 'delay']),
				width,
				height
			};
			this._setParams(elem, params);
			if (options.scale) elem.classList.add(`animation-show-start_${options.direction}`);
			if (options.fade) elem.classList.add('animation-show-start_fade');
			elem.offsetHeight; // reflow
			setTimeout(() => {
				elem.classList.add('animation-show');
			});

		});

		setTimeout(() => {
			forEach(elements, (elem) => {
				this._clearClasses(elem);
				if (options.updateSlider) elem.closest('.swiper')?.swiper?.update();
			});
			options.callback(elements);
		}, options.duration + options.delay);
	};

	/**
	 * Метод скрытия элементов
   * @param elements {HTMLElement[]} - HTML элементы
   * @param options {RemoveOptions} Параметры анимации
	 * @private
	 */
	static _hide = (elements, options) => {
		forEach(elements, (elem) => {
			if (elem.classList.contains('animation-hidden')) return;
			this._clearClasses(elem);
			elem.classList.add('animation-hide-start');
			const width = elem.offsetWidth;
			const height = elem.offsetHeight;
			const params = {
				...pick(options, ['duration', 'delay']),
				width,
				height
			};
			this._setParams(elem, params);
			if (options.scale) elem.classList.add(`animation-hide-start_${options.direction}`);
			if (options.fade) elem.classList.add('animation-hide-start_fade');
			elem.offsetHeight; // reflow
			elem.classList.add('animation-hide');
		});

		setTimeout(() => {
			forEach(elements, (elem) => {
				this._clearClasses(elem);
				elem.classList.add('animation-hidden');
				if (options.updateSlider) elem.closest('.swiper')?.swiper?.update();
			});
			options.callback(elements);
		}, options.duration + options.delay);
	};

	/**
	 * Очистка классов элемента
	 * @param elem {HTMLElement} - анимированный элемент
	 * @private
	 */
	static _clearClasses = (elem) => {
		elem.classList.remove('animation-hidden');

		// Удалить классы hide
		elem.classList.remove('animation-hide-start');
		elem.classList.remove('animation-hide-start_fade');
		elem.classList.remove('animation-hide-start_top');
		elem.classList.remove('animation-hide-start_right');
		elem.classList.remove('animation-hide-start_bottom');
		elem.classList.remove('animation-hide-start_left');
		elem.classList.remove('animation-hide');
		elem.classList.remove('animation-hide-end');

		// Удалить классы show
		elem.classList.remove('animation-show-start');
		elem.classList.remove('animation-show-start_fade');
		elem.classList.remove('animation-show-start_top');
		elem.classList.remove('animation-show-start_right');
		elem.classList.remove('animation-show-start_bottom');
		elem.classList.remove('animation-show-start_left');
		elem.classList.remove('animation-show');
		elem.classList.remove('animation-show-end');
	};

	/**
	 * Установка параметров анимации
	 * @param elem {HTMLElement} - анимированный HTML элемент
	 * @param params {Object} - объект с параметрами
	 * @private
	 */
	static _setParams = (elem, params) => {
		forEach(params, (value, key) => {
			elem.style.setProperty(`--${key}`, value);
		});
	};

  /**
   * Получение параметров
   * @param options {RemoveOptions} Параметры анимации
   * @return options {RemoveOptions} Параметры анимации
   * @private
   */
  static _getOptions = (options) => {
    return  merge({
      duration: 500,
      delay: 0,

      fade: false,
      scale: true,
      direction: 'right',

      updateSlider: false,
      callback: () => {}
    }, options);
  };


	/**
	 * Получение HTML элементов
	 * @param elem {HTMLElement|HTMLElement[]|String} - HTML элемент/ы или селектор
	 * @return {HTMLElement[]} - Найденные HTML элементы
	 * @private
	 */
	static _getElements = (elem) => {
		let elements = [];
		if (isArray(elem)) {
			elements = filter(elem, (item) => {
				if (this._isElement(item)) return true;
				else this._BlockError.errorMessage(`${item} не является HTML элементом`);
				return false;
			});
		} else if (this._isElement(elem)) {
			elements.push(elem);
		} else if (typeof elem === 'string') {
			elements = toArray(document.querySelectorAll(elem));
			if (isEmpty(elements)) this._BlockError.throwError('Элементы не найдены');
		} else {
			this._BlockError.throwError('Элементы не найдены');
		}
		return elements;
	};

	/**
	 * Проверка это объект является HTML элементом
	 * @param element {HTMLElement|String} - значение для проверки
	 * @return {boolean} - результат проверки
	 * @private
	 */
	static _isElement = (element) => {
		return (
			typeof HTMLElement === 'object' ? element instanceof HTMLElement :
				element && typeof element === 'object' && true && element.nodeType === 1 && typeof element.nodeName==='string'
		);
	};
}

// Экспорт;
window.Block.Animation = Animation;
